import { FetchError } from 'ofetch';
import { type FC, useEffect, useRef } from 'react';
import type { FormState, UseFormSetError } from 'react-hook-form';
import { parseError } from '@/base/sentry/utils';
import { Banner } from './Banner';
import { cn } from './react';

export const setFormErrors =
  (setError: UseFormSetError<never>) =>
  (e?: unknown): string[] => {
    const messages: string[] = [];
    if (e instanceof FetchError) {
      const data = typeof e.data === 'object' && e.data ? e.data : {};
      Object.entries(data).forEach(([key, val]) => {
        const errKey =
          key === 'non_field_errors' || key === 'detail' ? 'root' : key;
        const errMsg =
          typeof val === 'string' ? [val] : Array.isArray(val) ? val : [];
        if (errMsg.length && errKey) {
          messages.push(...(errMsg as string[]));
        }
      });
    }
    // Nothing field-specific came back, so fall back to the humanized copy
    // rather than surfacing a status code or a raw JS error message.
    if (messages.length === 0) {
      messages.push(parseError(e).message);
    }
    setError('root', {
      message: messages.join('\n'),
    });
    return messages;
  };

export const FormErrors: FC<{
  className?: string;
  errors?: FormState<any>['errors'] | undefined;
}> = ({ errors: formErrors, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const errors = Object.values(formErrors ?? {})
    .flatMap((x) => x?.message?.toString().split('\n'))
    .filter((x) => !!x) as string[];
  useEffect(() => {
    if (errors.length > 0 && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const partiallyVisible = rect.top < window.innerHeight && rect.bottom > 0;
      if (partiallyVisible) return;
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors.length]);
  if (errors.length === 0) return null;
  return (
    <div className={cn('w-full', className)} ref={ref}>
      <Banner
        variant="negative"
        className="w-full items-start [&>div]:items-start"
      >
        {errors.length === 1 ? (
          <p className="whitespace-pre-line">{errors[0]}</p>
        ) : (
          <ul className="list-disc space-y-1 ps-4 whitespace-pre-line">
            {errors.map((msg, i) => (
              <li key={`${msg}-${i.toString()}`}>{msg}</li>
            ))}
          </ul>
        )}
      </Banner>
    </div>
  );
};
